import {
  bookingEnd,
  bookingStart,
  closestBooking,
  connectedBookings,
  inEndOrder,
  nextBooking,
  rootBooking
} from '@/utils/bookings';
import {formatDateRelative, maxDate, minDate, minutes, toTime} from '@/utils/dates';
import {absentProperty, calcPrivateValue} from '@/utils/privacy';
import {mapActions, mapGetters} from 'vuex';
import {bookings as impl} from '@/room-booking/impl';
import {differenceInCalendarDays, endOfDay} from 'date-fns';

export default forProperty();

/**
 * Adds properties to the vue instance that help when displaying bookings. Pass the property name where the booking can
 * be found.
 *
 * @param {string} [bookingProp] defaults to 'booking'
 * @return {Object}
 */
export function forProperty(bookingProp = 'booking') {
  return {
    data() {
      return {
        bookingActionInProgress: false
      };
    },
    computed: {
      ...mapGetters('time', {
        clockTime: 'now' // we this only for "is same day" checks
      }),
      ...mapGetters('config', ['timeFormat']),
      // we use the bookings time instead of time/now for our local impression of the time as bookings.time updates
      // less often but at exactly the correct time to adjust all the state that is dependent on time (i.e. when
      // bookings would change)
      ...mapGetters('bookings', {
        _now: 'now',
        _knownBookings: 'knownBookings',
        _bookingByRef: 'bookingByRef',
        _reservationKeyForBooking: 'reservationKeyForBooking'
      }),
      _bookingValue() {
        return closestBooking(this[bookingProp], b => this._reservationKeyForBooking(b), this._workingTime);
      },
      _workingTime() {
        const bookingTime = this._now;
        if (!this.date) return bookingTime;
        const override = this.date;

        if (bookingTime.getDate() === override.getDate()) return bookingTime;

        return override;
      },
      reservationKey() {
        return this._reservationKeyForBooking(this._bookingValue);
      },
      isApproved() {
        const b = this._bookingValue;
        const reservationKey = this.reservationKey;
        const approval = impl.approval(b, reservationKey);
        return approval === undefined || approval && approval.status === 'APPROVED';
      },
      bookingTitle() {
        const prop = this.bookingTitleProp;
        return prop.exists ? prop.value : null;
      },
      bookingTitleProp() {
        const booking = this._bookingValue;
        if (!booking || !booking.title) return absentProperty;
        return calcPrivateValue(booking.privacy, booking.title, 'title');
      },
      bookingStart() {
        const booking = this._bookingValue;
        const reservationKey = this.reservationKey;
        if (!booking || !reservationKey) return null;
        return bookingStart(booking, reservationKey);
      },
      bookingEnd() {
        const booking = this._bookingValue;
        const reservationKey = this.reservationKey;
        if (!booking || !reservationKey) return null;
        return bookingEnd(booking, reservationKey);
      },
      bookingTimes() {
        return this.formatBookingTimes(this.bookingStart, this.bookingEnd);
      },
      bookingOwnerName() {
        const prop = this.bookingOwnerNameProp;
        return prop.exists ? prop.value : null;
      },
      bookingOwnerNameProp() {
        const booking = this._bookingValue;
        if (!booking || !booking.owner || !booking.owner.title) return absentProperty;
        return calcPrivateValue(booking.privacy, booking.owner.title, 'owner');
      },
      hasStarted() {
        const f = this.bookingStart;
        return f && f <= this._workingTime;
      },
      hasEnded() {
        const t = this.bookingEnd;
        return t && t < this._workingTime;
      },
      requiresCheckIn() {
        const booking = this._bookingValue;
        const reservationKey = this.reservationKey;
        if (!booking || !reservationKey) return null;
        return impl.requiresCheckIn(booking, reservationKey);
      },
      canCheckIn() {
        const f = this.bookingStart;
        return f && !this.checkedIn && (f.getTime() - this._workingTime.getTime()) <= (10 * minutes);
      },
      checkedIn() {
        const booking = this._bookingValue;
        const reservationKey = this.reservationKey;
        if (!booking || !reservationKey) return false;
        return !this.requiresCheckIn || impl.hasCheckIn(booking, reservationKey);
      },
      siteId() {
        const booking = this._bookingValue;
        if (!booking) return '';
        return booking.ref.parent.parent.id;
      },
      nextBooking() {
        const last = this._bookingValue;
        if (!last) {
          return rootBooking(this._bookingByRef,
              nextBooking(this._knownBookings, b => this._reservationKeyForBooking(b), this._workingTime));
        } else {
          return rootBooking(this._bookingByRef,
              nextBooking(this._knownBookings, b => this._reservationKeyForBooking(b),
                  bookingEnd(last, this.reservationKey)));
        }
      },
      freeMinutes() {
        if (!this._bookingValue) return 0;
        const endTime = this.reservationEnd;
        let lastBooking = this._bookingValue;
        if (this.hasConnectedBookings) {
          const connectedBookings = inEndOrder(this.connectedBookings, b => this._reservationKeyForBooking(b));
          const nextConnection = connectedBookings[connectedBookings.length - 1];
          const connectionEnd = bookingEnd(nextConnection, this.reservationKey);
          if (connectionEnd > this._workingTime) {
            lastBooking = nextConnection; // only consider future connected bookings
          }
        }

        const end = bookingEnd(lastBooking, this.reservationKey);
        if (differenceInCalendarDays(end, this.clockTime) > 0 || end.getTime() === endOfDay(this.clockTime).getTime()) {
          return 0; // no free minutes today
        }

        const haystack = this._knownBookings;
        const next = nextBooking(haystack, b => this._reservationKeyForBooking(b), end);
        if (!next) return Infinity; // no next booking
        const fromTime = impl.fromTime(next, this._reservationKeyForBooking(next));
        return Math.trunc((toTime(fromTime) - endTime.getTime()) / minutes);
      },

      connectedBookings() {
        const booking = this._bookingValue;
        if (!booking) return [];
        return connectedBookings(booking, this._knownBookings);
      },

      hasConnectedBookings() {
        return this.connectedBookings.length > 0;
      },

      reservationStart() {
        return minDate(this.bookingStart, ...this.connectedBookings.map(b => bookingStart(b, this.reservationKey)));
      },
      reservationEnd() {
        return maxDate(this.bookingEnd, ...this.connectedBookings.map(b => bookingEnd(b, this.reservationKey)));
      }
    },
    methods: {
      ...mapActions('bookings', [
        'releaseOrCancel',
        'checkInBooking'
      ]),
      async checkIn() {
        const booking = this._bookingValue;
        if (!booking || !booking.ref) {
          throw new Error(`no booking ref`);
        }
        this.bookingActionInProgress = true;
        try {
          await this.checkInBooking({booking});
        } catch (e) {
          this.$notify.showError(`Error checking in`);
          this.$logger.error(`error checking in booking`, e);
        } finally {
          this.bookingActionInProgress = false;
        }
      },
      async release() {
        const booking = this._bookingValue;
        if (!booking || !booking.ref) {
          throw new Error(`no booking ref`);
        }
        this.bookingActionInProgress = true;
        try {
          await this.releaseOrCancel({booking, release: this.hasStarted});
        } catch (e) {
          const verb = this.hasStarted ? 'releasing' : 'cancelling';
          this.$notify.showError(`Error ${verb} booking`);
          this.$logger.error(`error ${verb} booking`, e);
        } finally {
          this.bookingActionInProgress = false;
        }
      },

      formatBookingTimes(start, end) {
        if (!start || !end) {
          return '';
        }
        const now = this.clockTime;
        let from = formatDateRelative(start, now);
        let to = formatDateRelative(end, now);
        if (start.getDate() === end.getDate()) {
          to = '';
        }
        if (from !== '') {
          from += ' ';
        }
        if (to !== '') {
          to += ' ';
        }
        from += this.formatTime(start);
        to += this.formatTime(end);
        return `${from} - ${to}`;
      }
    }
  };
}
